import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { caixaService } from "@/features/caixa/services/caixaService";
import { SelectCadastroRh } from "./SelectCadastroRh";

/** Caixas conhecidos (`GET /api/caixa/caixas`), com cache compartilhado entre telas. */
export function useCaixas() {
  return useQuery({ queryKey: ["caixa-caixas"], queryFn: caixaService.listarCaixas, staleTime: 60_000 });
}

/** Nome do caixa para tabelas: "Caixa 1 (nº 105)" / "Caixa 01". */
export function useNomeCaixa() {
  const { data = [] } = useCaixas();
  return useCallback(
    (id: number | null | undefined) => {
      if (id == null || id <= 0) return "—";
      const c = data.find((x) => x.id === id);
      if (!c) return `Caixa ${String(id).padStart(2, "0")}`;
      return c.terminal ? `${c.nome} (nº ${c.id})` : c.nome;
    },
    [data],
  );
}

interface CaixaSelectProps {
  /** undefined = "todos" (só com `rotuloTodos`). */
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  /** Mostra a opção "todos" (filtros). */
  rotuloTodos?: string;
  /** Caixa a esconder (ex.: a origem, no select de destino). */
  excluirId?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Seletor de caixa por nome. Não existe cadastro de caixa (como no Desktop, o caixa é o
 * número da estação): a lista vem dos caixas com movimento + terminais do CashBox.
 * Inativos (terminal revogado) só aparecem se já estiverem selecionados.
 */
export function CaixaSelect({ excluirId, placeholder = "Selecione o caixa", ...props }: CaixaSelectProps) {
  const { data = [], isLoading } = useCaixas();
  const opcoes = data
    .filter((c) => c.id !== excluirId)
    .map((c) => ({ id: c.id, rotulo: c.nome, detalhe: c.terminal ? `nº ${c.id}` : undefined, ativo: c.ativo }));
  return (
    <SelectCadastroRh
      {...props}
      rotuloNenhum={null}
      placeholder={isLoading ? "Carregando caixas…" : placeholder}
      opcoes={opcoes}
    />
  );
}
